
import '../App.css';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { UserDashboard } from '../components/UserDashboard';


export const TabsExtended = ({
  children,
  defaultIndex = 0,
  onChange,
  className = "",
}) => {
  const tabs = React.Children.toArray(children);
  const [activeIndex, setActiveIndex] = useState(defaultIndex);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  const listRef = useRef(null);
  const btnRefs = useRef([]);

  // Ensure the refs array length matches the number of tabs
  btnRefs.current = useMemo(
    () => tabs.map((_, i) => btnRefs.current[i] ?? React.createRef()),
    [tabs.length]
  );

  useEffect(() => {
    const el = btnRefs.current[activeIndex]?.current;
    if (!el) return;
    const { offsetLeft, offsetWidth } = el;
    setIndicatorStyle({ left: offsetLeft, width: offsetWidth });

    // Scroll active tab into view (nice when tabs overflow)
    el.scrollIntoView({ inline: "center", behavior: "smooth", block: "nearest" });
  }, [activeIndex, tabs.length]);

  const setIdx = (i) => {
    setActiveIndex(i);
    onChange?.(i);
  };

  // Keyboard navigation: Left/Right/Home/End
  const onKeyDown = (e) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;

    e.preventDefault();
    const last = tabs.length - 1;

    if (e.key === "ArrowRight") setIdx(activeIndex === last ? 0 : activeIndex + 1);
    if (e.key === "ArrowLeft") setIdx(activeIndex === 0 ? last : activeIndex - 1);
    if (e.key === "Home") setIdx(0);
    if (e.key === "End") setIdx(last);

    // Focus the new active button for a11y
    requestAnimationFrame(() => {
      btnRefs.current[activeIndex]?.current?.focus();
    });
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Tablist */}
      <div
        ref={listRef}
        role="tablist"
        aria-orientation="horizontal"
        onKeyDown={onKeyDown}
        className="
          relative
          flex gap-2
          overflow-x-auto
          rounded-t-2xl
          bg-white/50 dark:bg-white/5
          p-2
          backdrop-blur
          ring-1 ring-black/5 dark:ring-white/10
        "
      >
        {/* Animated underline/indicator */}
        <div
          aria-hidden="true"
          className="
            pointer-events-none absolute bottom-0 h-0.5
            bg-gradient-to-r from-cyan-400 via-indigo-400 to-fuchsia-400
            transition-[left,width] duration-300 ease-out
          "
          style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
        />

        {tabs.map((tab, idx) => {
          const title = tab.props.title ?? `Tab ${idx + 1}`;
          const isActive = idx === activeIndex;
          const id = `tab-${idx}`;
          const panelId = `panel-${idx}`;

          return (
            <button
              key={id}
              ref={btnRefs.current[idx]}
              id={id}
              role="tab"
              aria-selected={isActive}
              aria-controls={panelId}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setIdx(idx)}
              className={[
                "relative",
                "px-6 py-3 text-base md:text-lg font-semibold",
                "rounded-xl",
                "transition-colors duration-200",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70",
                isActive
                  ? "text-white dark:text-white"
                  : "text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white",
                isActive
                  ? "bg-gradient-to-b from-white/70 to-white/40 dark:from-white/10 dark:to-white/5"
                  : "bg-white/40 hover:bg-white/60 dark:bg-white/0 dark:hover:bg-white/5",
              ].join(" ")}
            >
              {/* Subtle glow when active */}
              {isActive && (
                <span
                  className="
                    pointer-events-none absolute inset-0 -z-10
                    rounded-xl shadow-[0_0_24px_-6px_rgba(56,189,248,0.35)]
                    dark:shadow-[0_0_24px_-6px_rgba(129,140,248,0.4)]
                  "
                />
              )}
              {title}
            </button>
          );
        })}
      </div>

      {/* Panels */}
      <div
        className="
          rounded-b-2xl
          bg-white/60 dark:bg-white/[0.03]
          p-4 md:p-6
          ring-1 ring-black/5 dark:ring-white/10
        "
      >
        {tabs.map((tab, idx) => {
          const isActive = idx === activeIndex;
          const id = `panel-${idx}`;
          const tabId = `tab-${idx}`;
          return (
            <div
              key={id}
              id={id}
              role="tabpanel"
              aria-labelledby={tabId}
              hidden={!isActive}
              className="animate-fadeIn"
            >
              {isActive && tab}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const Dashboard = ({config}) => {
  return (
    <>
      <UserDashboard config={config} /> 
    </>
  );
}

